/**
 * Behavior-related exports
 * Import from 'trace-viewer/behaviors'
 */

// Ensure all built-in behaviors are registered before any registry access.
// Exported to create a live dependency that prevents tree-shaking of registrations.
import { registerBuiltInBehaviors } from '../behaviors/registerAll';
export const BUILT_IN_BEHAVIOR_COUNT = registerBuiltInBehaviors();

// Base type and registry
export type { TraceBehavior } from '../behaviors/tracebehavior';
export type { BindingDiagnostic } from '../behaviors/tracebehavior';
// Binding detection — editors use these to tell binding strings ("$material",
// "{theme}") apart from literal values (e.g. asset URLs) in authored data,
// and to surface what a bound field points at.
export { isPropertyBinding, boundPropertyName } from '../behaviors/tracebehavior';
export type { BehaviorConstructor, DataOf } from '../behaviors/BehaviorRegistry';
export { getBehaviorConstructor, registerBehavior } from '../behaviors/BehaviorRegistry';

// Scripting
export { ScriptBehavior } from '../scripting/ScriptBehavior';
export { ScriptRegistry } from '../scripting/ScriptRegistry';
export { SCRIPT_API_TYPES } from '../scripting/generated-api-docs';
export { dryRunHooks } from '../scripting/ScriptCompiler';

// Behavior classes
export { Animation } from '../behaviors/animation';
export { AnimationSequence, AnimationStateHandle } from '../behaviors/animationsequence';
export { AudioClip } from '../behaviors/audioclip';
export { AudioSource } from '../behaviors/audiosource';
export { AutoUI, setAutoUISwatchEditorCommit } from '../behaviors/autoui';
export { Billboard } from '../behaviors/billboard';
export { Tether } from '../behaviors/tether';
export { ViewAnchor } from '../behaviors/viewanchor';
export { isRuntimeToggleable } from '../behaviors/runtimeToggleable';
export type { RuntimeToggleable } from '../behaviors/runtimeToggleable';
export { BoxCollider } from '../behaviors/boxcollider';
export { ContentSlot } from '../behaviors/contentslot';
export { Frame } from '../behaviors/frame';
export { Image } from '../behaviors/image';
export { Bevel } from '../behaviors/bevel';
export { MorphStretch, type MorphStretchAxisCapability } from '../behaviors/morphstretch';
export { MeshBackground } from '../behaviors/meshbackground';
export { ImageAnchor } from '../behaviors/imageanchor';
export { Layout } from '../behaviors/layout';
export type { LayoutSizeProvider, LayoutSizeReceiver, LayoutFit, LayoutContentFrame } from '../behaviors/layout';
export { LayoutItem } from '../behaviors/layoutitem';
export { MarkerSlot } from '../behaviors/markerslot';
export { Model } from '../behaviors/model';
export { Moveable } from '../behaviors/moveable';
export { Keyframes } from '../behaviors/keyframes';
export { Pages } from '../behaviors/pages';
export { Rotate } from '../behaviors/rotate';
export { Rotatable } from '../behaviors/rotatable';
export { ParticleSystem } from '../behaviors/particlesystem';
export { Properties, type PropertyValue } from '../behaviors/properties';
export { Provenance } from '../behaviors/provenance';
export { AiSession } from '../behaviors/aisession';
export { RecordingInfo } from '../behaviors/recordinginfo';
export { RestRelativeBinding } from '../behaviors/restrelativebinding';
export { Renderer } from '../behaviors/renderer';
export { SphereCollider } from '../behaviors/spherecollider';
export { Text } from '../behaviors/text';
export { Video, CONTROLS_MESH_SUFFIX } from '../behaviors/video';
export { Synth } from '../behaviors/synth';
export { Timeline } from '../behaviors/timeline';
export { BehaviorGraph } from '../behaviors/behaviorgraph/behaviorgraph';
export { GraphBuilder } from '../behaviors/behaviorgraph/GraphBuilder';
export type { FlowChain, HoverChain, AnimationChain, BranchChain, SequenceChain, DelayChain, ProximityChain, IntervalChain } from '../behaviors/behaviorgraph/GraphBuilder';
export { BLOCK_DEFS, UNLISTED_BLOCK_DEFS, getBlockDef, getBlocksByCategory, getBlocksByDocGroup } from '../behaviors/behaviorgraph/blockCatalog';
export { findGraphCycles } from '../behaviors/behaviorgraph/graphOrdering';
export type { BlockDef, BlockCategory, BlockRegistration, MacroOp, SocketType, SocketDef, ConfigFieldDef } from '../behaviors/behaviorgraph/blockCatalog';
export { inlineSubgraphs, hasSubgraphDeclarations, TRACE_SUBGRAPH_KEY, SUBGRAPH_INDEX_KEY, SUBGRAPH_MANIFEST_KEY } from '../behaviors/behaviorgraph/subgraphInline';
export type { SubgraphManifest } from '../behaviors/behaviorgraph/subgraphInline';
export { recollapseSubgraphs, hasSubgraphTags } from '../behaviors/behaviorgraph/subgraphRecollapse';
export { expandMacro, recognizeMacros, raiseMacros, raiseBehaviorGraphData, registerMacro, MACRO_PARAM_WIRED, getMacro, getMacroByOp, allMacros, lowerMacros, lowerBehaviorGraphData, MACRO_OP_PREFIX, TRACE_MACRO_KEY, MACRO_ID_KEY, MACRO_ROLE_KEY } from '../behaviors/behaviorgraph/macros';
export type { MacroDefinition, MacroShape, MacroNodeDef, MacroWireDef, MacroValueWireDef, MacroSocketRef, MacroParamDef, MacroRoleNodes, MacroExpansion, MacroInstance, MacroWiredValue, MacroGraphWriter, MacroEventDef, MacroVariableDef, RaiseMacrosOptions, ExpandMacroOptions } from '../behaviors/behaviorgraph/macros';
export { expandIntervalMacro, recognizeIntervalMacros, INTERVAL_MACRO, INTERVAL_MACRO_OP, INTERVAL_MACRO_DEF } from '../behaviors/behaviorgraph/macros';
export { TOGGLE_ACTIVE_MACRO, TOGGLE_ACTIVE_MACRO_OP, TOGGLE_ACTIVE_MACRO_DEF, TOGGLE_ACTIVE_TARGET_PARAM } from '../behaviors/behaviorgraph/macros';
export type { IntervalMacroNodes, IntervalMacroInstance, IntervalMacroRole } from '../behaviors/behaviorgraph/macros';
// BEHAVIOR_GRAPH_BLOCKS_PROMPT lives in `trace-viewer/behaviors/ai-prompts` —
// authoring-time only, kept off the runtime subpath to avoid bundling.
export { isPlayable, hasPlayableClock } from '../behaviors/playable';
export type { Playable, PlayableClock } from '../behaviors/playable';

// Behavior data types
export type { BehaviorData } from '../scenedata/behaviordata';
export type { AnimationData } from '../scenedata/behaviors/animationdata';
export type { AnimationSequenceData, AnimationStateConfig, AnimationSequenceStates } from '../scenedata/behaviors/animationsequencedata';
export type { AudioClipData } from '../scenedata/behaviors/audioclipdata';
export type { AudioSourceData, AudioRolloffMode } from '../scenedata/behaviors/audiosourcedata';
export type { AutoUIData } from '../scenedata/behaviors/autouidata';
export type { BillboardData, BillboardOnExit } from '../scenedata/behaviors/billboarddata';
export type { TetherData } from '../scenedata/behaviors/tetherdata';
export type { ViewAnchorData } from '../scenedata/behaviors/viewanchordata';
export type { BoxColliderData } from '../scenedata/behaviors/boxcolliderdata';
export type { ContentSlotData, ContentSlotFit } from '../scenedata/behaviors/contentslotdata';
export type { FrameData } from '../scenedata/behaviors/framedata';
export type { ImageData } from '../scenedata/behaviors/imagedata';
export type { BevelData } from '../scenedata/behaviors/beveldata';
export type { MorphStretchData } from '../scenedata/behaviors/morphstretchdata';
export type { MeshBackgroundData } from '../scenedata/behaviors/meshbackgrounddata';
export type { ImageAnchorData } from '../scenedata/behaviors/imageanchordata';
export { AnchorOrientation } from '../scenedata/common';
export type { LayoutData, LayoutPaddingData } from '../scenedata/behaviors/layoutdata';
export type { LayoutItemData } from '../scenedata/behaviors/layoutitemdata';
export type { MarkerSlotData } from '../scenedata/behaviors/markerslotdata';
export type { ModelData } from '../scenedata/behaviors/modeldata';
export type { MoveableData } from '../scenedata/behaviors/moveabledata';
export type { KeyframesData, KeyframeDefinition, KeyframeEntry } from '../scenedata/behaviors/keyframesdata';
export type { PagesData, PageData, StateAssignment, PageSource } from '../scenedata/behaviors/pagesdata';
export { PAGE_STATE_ENTER, PAGE_STATE_EXIT } from '../scenedata/behaviors/pagesdata';
export type { RotateData, RotateAxis } from '../scenedata/behaviors/rotatedata';
export type { RotatableData } from '../scenedata/behaviors/rotatabledata';
export type { ParticleSystemData, ParticleShape, ParticleNoise, Range, Keyframe } from '../scenedata/behaviors/particledata';
export { ParticleShapeType, ParticleTextureMode, ParticleBlendMode } from '../scenedata/behaviors/particledata';
export type { PropertiesData, PropertyDeclaration, StringDeclaration, NumberDeclaration, BooleanDeclaration, Vector3Declaration, Vector4Declaration, ColorDeclaration, TransitionConfig, TimeTransition, PropertyTransition, EasingFunction, StateDefinition, TransformOverride, TransformComponent, ResolvedPose } from '../scenedata/behaviors/propertiesdata';
export type { PropertySheet } from '../scenedata/behaviors/propertiesdata';
export { isTimeTransition, isPropertyTransition } from '../scenedata/behaviors/propertiesdata';
// Transform components may be bound (`[1, "{stageHeight}", 1]`), so editors that
// do pose math have to ask whether a tuple is numeric before assuming it.
export { numericTuple, hasBoundComponent } from '../scenedata/behaviors/propertiesdata';
export { parsePropertySheet, PROPERTY_SHEET_VERSION } from '../scenedata/behaviors/propertiesdata';
export type { ProvenanceData, ProvenanceSource, ProvenanceAsset } from '../scenedata/behaviors/provenancedata';
export type { AiSessionData } from '../scenedata/behaviors/aisessiondata';
export type { RecordingInfoData, RecordingAuthor, RecordingCapture, RecordingFrameInfo } from '../scenedata/behaviors/recordinginfodata';
export type { RestRelativeBindingData } from '../scenedata/behaviors/restrelativebindingdata';
export type { RendererData, MaterialData } from '../scenedata/behaviors/rendererdata';
export type { SphereColliderData } from '../scenedata/behaviors/spherecolliderdata';
export type { TextData, BundledFontFamily, FontFamily, FontWeight, FontStyle, FontVariant } from '../scenedata/behaviors/textdata';

// Hosted fonts — register typefaces from the asset library at runtime instead
// of bundling their atlases. The package declares no packs: a host names the
// families its deployment publishes with `declareFontPacks` at boot (or loads
// one eagerly with `loadFontPackFromUrl`), and Text picks the faces up without
// polling, re-measuring any paragraph already laid out against a fallback.
// `ensureFontFamily` is the on-demand fetch the Text behavior already
// triggers, exposed for a host that wants a family warm before it draws (a
// bake, which cannot wait for a late arrival): it returns the load, so await
// it. `undeclareFontFamily` withdraws a family that will never load.
//
// The catalogue is page-wide: one set per page, shared by every viewer on it.
// A per-viewer setting (an attribute on each `<trace-viewer>`) can differ
// between two viewers on one page; the font catalogue cannot.
export {
    registerFace,
    loadFace,
    loadFontPack,
    loadFontPackFromUrl,
    onFontRegistryChange,
    registeredRevision,
    declareFontPacks,
    undeclareFontFamily,
    declaredFontFamilies,
    declaredFontPack,
    ensureFontFamily,
    retryFontFamily,
    onHostedFontFailure,
    hostedFontFailures,
} from '../behaviors/text/hostedFonts';
export type {
    HostedFontFace,
    HostedFontPack,
    HostedFontLoadResult,
    HostedFontSkipReason,
    HostedFontPackFailure,
} from '../behaviors/text/hostedFonts';
// A paragraph drawn in a face nobody asked for is the failure this whole area
// exists to remove, and the log line reporting it is below every host's log
// level. `onFontSubstitution` is that report as data.
export { onFontSubstitution } from '../behaviors/text/fontRegistry';
export type { FontSubstitution, FontSubstitutionReason } from '../behaviors/text/fontRegistry';
// `registerFace` takes a parsed definition, so a consumer with its own asset
// pipeline needs to be able to name the shape.
export type { BmFontJson } from '../behaviors/text/bmfont';
export type { VideoData } from '../scenedata/behaviors/videodata';
export type { SynthData, SynthWaveform } from '../scenedata/behaviors/synthdata';
export type { TimelineData, TimelineKeyframe } from '../scenedata/behaviors/timelinedata';
export { GraphOps } from '../scenedata/behaviors/behaviorgraphdata';
export { recordingActivationGraph, recordingActivationBehavior, RECORDING_TRACKS_PATH } from '../scenedata/recordingActivation';
export type { RecordingActivationOptions } from '../scenedata/recordingActivation';
export type { GraphOp, BehaviorGraphData, GraphData, GraphNode, Declaration, TypeDefinition, GraphVariable, GraphEvent, ValueInput, FlowTarget, ConfigValue, SubgraphRef, FlowSocketMapping, ValueSocketDeclaration, OutputValueSocketDeclaration } from '../scenedata/behaviors/behaviorgraphdata';
export type { TransformKeyframe } from '../scenedata/behaviors/transformkeyframe';

// Material slot type (editor utility)
export type { MaterialSlot } from '../editor/MaterialPreviews';

// Schema system
export type { ActionSchema, BehaviorSchema, FieldSchema, FieldType, FieldValueMap } from '../scenedata/schemas/types';
export { BEHAVIOR_SCHEMAS, getBehaviorSchema, getAddableBehaviorTypes } from '../scenedata/schemas/index';
export { formatBehaviorSchema } from '../scenedata/schemas/format';
export { createDefaultData } from '../scenedata/schemas/defaults';

// SceneCreation for behavior instantiation
export { SceneCreation } from '../loaders/SceneCreation';

export { validateExecutableGraph, assertExecutableGraph, GraphValidationError } from '../behaviors/behaviorgraph/graphValidation';
export type { GraphDiagnostic } from '../behaviors/behaviorgraph/graphValidation';
export type { ExpressionType, GraphExpressionValue } from '../behaviors/behaviorgraph/graphExpression';
